import { useRhAuth } from '../../../contexts/RhAuthContext';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import { FiEdit2 } from 'react-icons/fi';
import { CircleSpinner } from 'react-spinners-kit';
import styled from 'styled-components';
import { getRoleDescription } from '../../../util/role-description';
import { useUserRoles } from '../../../util/use-user-role';
import { DashboardHeader } from '../../core/DashboardHeader';
import { DashboardWrapper } from '../../core/DashboardWrapper';
import { Can } from '../../core/rbac/Can';
import { RhButton, RhButtonMode } from '../../core/RhButton';
import { RhCard } from '../../core/RhCard';
import { RhImage } from '../../core/RhImage';
import { RhInput, RhInputMode } from '../../core/RhInput';
import { RhSelect } from '../../core/RhSelect';
import { SizedBox } from '../../core/SizedBox';
import { colors, H1, H2, H5, Regular } from '../../core/Styles';
import { ViewPersonProps } from '../containers/ViewPersonContainer';

const NAME_MAX_LENGTH = 20;
const NAME_ALLOWED = /^[a-zA-Z0-9 .]*$/;

export const ViewPersonScreen: React.FC<ViewPersonProps> = props => {
  const { roles: myRoles, loading: isGettingRoles } = useUserRoles();
  const { user } = useRhAuth();
  const [isEditingName, setIsEditingName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const isMe = user?.email === props.user?.email;
  const userRole = props.user?.role;
  const roleOptions = props.roles;
  const myRole = myRoles?.[0];
  const isDeleteVisible =
    !props.loading &&
    props!.isSaveDisabled &&
    !isGettingRoles &&
    !isMe &&
    (myRole === 'System Admin' || (myRole === 'Church Admin' && userRole !== 'System Admin'));

  const currentUserRoleOptions: { label: string; value: string }[] = useMemo(() => {
    if (isMe) {
      return [{ label: myRole || '', value: myRole || '' }];
    }

    if (!roleOptions) {
      return [];
    }

    switch (userRole) {
      case 'System Admin':
        return [{ label: userRole, value: userRole }];
      default:
        return roleOptions
          .filter(value => value !== 'System Admin')
          .map(role => ({
            label: role,
            value: role,
          }));
    }
  }, [roleOptions, userRole, isMe, myRole]);

  return (
    <DashboardWrapper>
      <DashboardHeader
        title={
          <Breadcrumb>
            People <Slash>/</Slash> {_.startCase(props.user?.name) || '-'}
          </Breadcrumb>
        }
        actions={[
          props.loading && <CircleSpinner key="view-person-loader" size={15} color={colors.primary} />,
          <RhButton
            key="view-person-action-1"
            mode={RhButtonMode.text}
            title="Cancel"
            onClick={props.userClickedCancel}
            disabled={props.isCancelDisabled}
          />,
          <RhButton
            key="view-person-action-2"
            mode={RhButtonMode.primary}
            title="Save Changes"
            disabled={props.loading || props.isSaveDisabled || !props.formState.name.trim()}
            onClick={props.userClickedSave}
          />,
        ]}
      />
      <Grid>
        <RhCard>
          <UserCardDiv>
            <RhImage src={props.user?.imagePath || ''} width={218} height={218} radius={109} />
            <SizedBox height={12} />
            {isEditingName ? (
              <RhInput
                inputRef={nameInputRef}
                mode={RhInputMode.subtle}
                placeholder="Enter name..."
                value={props.formState.name}
                maxLength={NAME_MAX_LENGTH}
                autoFocus
                onChange={e => {
                  const val = e.target.value;
                  if (NAME_ALLOWED.test(val)) {
                    props.userUpdatedFormState(f => ({ ...f, name: val }));
                  }
                }}
                onBlur={() => setIsEditingName(false)}
                onKeyDown={e => {
                  if (e.key === 'Enter') setIsEditingName(false);
                }}
                style={{ textAlign: 'center', width: '100%' }}
              />
            ) : (
              <NameRow>
                <H1 style={{ textAlign: 'center' }}>{props.formState.name || '-'}</H1>
                <PencilButton
                  onClick={() => {
                    setIsEditingName(true);
                    setTimeout(() => nameInputRef.current?.focus(), 0);
                  }}>
                  <FiEdit2 size={15} color={colors.grey} />
                </PencilButton>
              </NameRow>
            )}
            <SizedBox height={12} />
            <RhButton mode={RhButtonMode.subtle} title={props.user?.role || '-'} />
            <SizedBox height={16} />
            <Regular weight="600" style={{ letterSpacing: 'unset' }}>
              {props.user?.email || '-'}
            </Regular>
            <SizedBox height={10} />
            <Regular weight="600" style={{ letterSpacing: 'unset' }}>
              Joined {dayjs(props.user?.created).format('MMMM DD, YYYY')}
            </Regular>
            <SizedBox height={10} />
            {/*<Regular weight="600" style={{ letterSpacing: 'unset' }}>No Affiliation1</Regular>*/}
          </UserCardDiv>
        </RhCard>
        <RhCard>
          <H2>User Permissions</H2>
          <Can
            role={myRole}
            perform="update:assign_church"
            yes={
              <>
                <H5>Church Affiliation</H5>
                <SizedBox height={2} />
                <Regular style={{ letterSpacing: 'unset' }}>
                  Users must be affiliated with a Church to create Programs and Events.
                </Regular>
                <SizedBox height={8} />
                <RhSelect
                  value={{
                    label: props?.churches?.filter(study => study.value === props.formState.churchId)[0]?.label || '',
                    value: props.formState.churchId,
                  }}
                  placeholder="Add user to an existing church..."
                  isSearchable={true}
                  options={props.churches || []}
                  optionsStyle={{ fontSize: 14 }}
                  onChange={option =>
                    props.userUpdatedFormState(f => ({
                      ...f,
                      churchId: option.value,
                    }))
                  }
                />
              </>
            }
          />
          {/* This is not part of PHASE1 */}

          <SizedBox height={24} />
          <H5>CONTENT PRIVILEGES</H5>
          <SizedBox height={11} />
          <SelectRolesDiv>
            <RhSelect
              placeholder="Select role..."
              value={{
                label: props.formState.role,
                value: props.formState.role,
              }}
              isSearchable={true}
              options={currentUserRoleOptions}
              optionsStyle={{ fontSize: 14 }}
              // This width depends on the longest label of an option
              containerSyle={{ width: '20ch' }}
              onChange={option =>
                props.userUpdatedFormState(f => ({
                  ...f,
                  role: option.value,
                }))
              }
            />
            <Regular weight="600" color={colors.grey} style={{ letterSpacing: 'unset' }}>
              {getRoleDescription(props.formState.role)}
            </Regular>
          </SelectRolesDiv>
          <SizedBox height={24} />
          {isDeleteVisible && (
            <Can
              role={myRole}
              perform="delete:users"
              yes={
                <>
                  <H2 className="text-red-700">Danger Zone</H2>
                  <SizedBox height={11} />
                  <H5>Delete this user</H5>
                  <Regular>Once you delete a user, there is no going back. Please be certain.</Regular>
                  <SizedBox height={11} />
                  <button
                    className="min-h-[38px] border border-gray-[#ccc] rounded-[10px] text-sm py-[2px] px-2"
                    onClick={props.userClickedDeletePerson}>
                    <strong className="text-red-700">Delete this user</strong>
                  </button>
                </>
              }
            />
          )}
        </RhCard>
      </Grid>
    </DashboardWrapper>
  );
};

const Breadcrumb = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: ${colors.normal};
`;

const Slash = styled.span`
  color: #c7cccf;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 2fr 6fr;
  grid-gap: 20px;
`;

const UserCardDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// const SelectPlaceholder = styled.div`
//   display: flex;
//   align-items: center;
//   & > :not(:last-child) {
//     margin-right: 8px;
//   }
// `;

const SelectRolesDiv = styled.div`
  display: flex;
  align-items: center;
  & > :not(:last-child) {
    margin-right: 8px;
  }
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PencilButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  opacity: 0.5;
  &:hover {
    opacity: 1;
  }
`;
